import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./LodestonePaywall.module.scss"

/**
 * LodestonePaywall (`/lodestone`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium.
 *
 * Divergence brief: claude-paywalls/_ledger.md. Concept doc: claude-paywalls/lodestone.md.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * Newest @Paywalls-Bot mention this run: `focus on security and privacy` (NOT the
 * reset keyword `default`) → an ACTIVE STANDING DIRECTIVE at super-high priority.
 * The whole concept is built to it — the pitch is containment: pull every private
 * photo OUT of the exposed places and into one sealed vault.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "Draw them all home." Right now your private photos are scattered across places
 * anyone can reach — your Camera Roll, iCloud Photos, a Shared album, some other
 * app's picker. This paywall hands you a LODESTONE: sweep it across your phone and
 * every private photo is drawn out of those exposed places and docked into a
 * single vault that seals behind its own Face ID / PIN. As each source empties it
 * is struck through — off the Camera Roll, out of iCloud, gone from Shared. When
 * the last one lands the vault SEALS. Auto-runs for passive viewers; honours
 * reduced motion; full light & dark.
 *
 * ── Divergence (vs. the mined security/privacy cluster) ─────────────────────
 * Layout   : a SPATIAL FIELD — four labelled "exposed source" zones scattered with
 *            loose photos, a sealed vault at the foot, a draggable lodestone. Not a
 *            console/plate (BothKeys), not strips swept private (Unseen), not a
 *            pile sorted into album trays (Tidy).
 * Interact : 2D MAGNET SWEEP — drag the lodestone anywhere; photos within its pull
 *            radius detach and fly home. New to the set: not a vertical pull, a
 *            single throw, a rotary dial, or a press-and-hold.
 * Value    : CONTAINMENT / removal-from-exposure — the win is that copies stop
 *            living in reachable places, not merely that a door is shut.
 * Metaphor : a lodestone (a natural magnet) that draws memories home. Unused.
 * Register : calm, gravitational, satisfying — memories in full colour the whole
 *            time; the drama is where they *live*, never hiding them from you.
 *
 * Honest & App-Store compliant: maps to real protections — moving photos into the
 * vault takes them out of the Camera Roll & shared albums; the vault has its own
 * Face ID / PIN lock separate from the phone's; private encrypted backup. Strictly
 * additive framing of a *move* (originals relocated into the vault, not deleted),
 * NO data-loss claim, no fake urgency/scarcity, honest Save %. Yearly is featured.
 */

type PlanKey = "yearly" | "monthly"

type SourceKey = "roll" | "icloud" | "shared" | "apps"

interface Photo {
  id: string
  img: string
  src: SourceKey
  x: number // % of field width
  y: number // % of field height
}

// Reuse existing bundled personal-memory photos. Shown in full colour throughout
// — the point is never to hide them from you, only to move them somewhere sealed.
const PHOTOS: Photo[] = [
  { id: "p1", img: "m01", src: "roll", x: 19, y: 24 },
  { id: "p2", img: "m06", src: "roll", x: 35, y: 17 },
  { id: "p3", img: "m03", src: "icloud", x: 81, y: 24 },
  { id: "p4", img: "m10", src: "icloud", x: 65, y: 17 },
  { id: "p5", img: "m14", src: "shared", x: 20, y: 77 },
  { id: "p6", img: "m17", src: "apps", x: 80, y: 77 },
]

// The exposed places your private photos leak into today. Each is a real surface
// the vault removes them from. Positioned near their photo clusters.
const SOURCES: {
  key: SourceKey
  label: string
  x: number
  y: number
}[] = [
  { key: "roll", label: "Camera Roll", x: 22, y: 17 },
  { key: "icloud", label: "iCloud Photos", x: 78, y: 17 },
  { key: "shared", label: "Shared album", x: 22, y: 87 },
  { key: "apps", label: "Other apps", x: 78, y: 87 },
]

// What the vault actually does once your photos are inside — real features.
const PROTECTS = [
  { k: "lock", label: "Its own Face ID or PIN — separate from your phone's" },
  { k: "off", label: "Taken out of your Camera Roll & shared albums" },
  { k: "backup", label: "Private, encrypted backup you alone can open" },
]

const clamp = (v: number, lo: number, hi: number) =>
  v < lo ? lo : v > hi ? hi : v

const LodestonePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = (() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 79
    return Math.round((1 - y / (m * 12)) * 100)
  })()

  const total = PHOTOS.length

  // captured photo ids
  const [captured, setCaptured] = useState<Set<string>>(() =>
    reduceMotion ? new Set(PHOTOS.map((p) => p.id)) : new Set(),
  )
  const [sealed, setSealed] = useState<boolean>(!!reduceMotion)
  const [dragging, setDragging] = useState(false)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)
  const [hint, setHint] = useState(false)

  // lodestone position, in field px. starts docked at the vault mouth.
  const [puck, setPuck] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const puckRef = useRef(puck)
  puckRef.current = puck

  const fieldRef = useRef<HTMLDivElement>(null)
  const dims = useRef<{ w: number; h: number }>({ w: 402, h: 520 })
  const capturedRef = useRef(captured)
  const sealedRef = useRef(sealed)
  const touched = useRef(false)
  const rafRef = useRef<number>(0)
  const timers = useRef<number[]>([])
  const pushTimer = (id: number) => timers.current.push(id)

  capturedRef.current = captured
  sealedRef.current = sealed

  // where the lodestone rests: just above the vault, centre-foot of the field.
  const restPos = useCallback(
    () => ({ x: dims.current.w * 0.5, y: dims.current.h * 0.9 }),
    [],
  )

  useLayoutEffect(() => {
    const el = fieldRef.current
    if (el) {
      const r = el.getBoundingClientRect()
      dims.current = { w: r.width, h: r.height }
    }
    setPuck(restPos())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopRaf = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = 0
  }

  // Capture any loose photo within the lodestone's pull radius.
  const pullAt = useCallback(
    (px: number, py: number) => {
      const { w, h } = dims.current
      const radius = w * 0.2
      let changed = false
      const next = new Set(capturedRef.current)
      for (const p of PHOTOS) {
        if (next.has(p.id)) continue
        const cx = (p.x / 100) * w
        const cy = (p.y / 100) * h
        if (Math.hypot(cx - px, cy - py) <= radius) {
          next.add(p.id)
          changed = true
        }
      }
      if (changed) {
        capturedRef.current = next
        setCaptured(next)
        native.logMixpanelEvent("html_lodestone_pull", { count: next.size })
        if (next.size >= total && !sealedRef.current) {
          pushTimer(
            window.setTimeout(() => {
              sealedRef.current = true
              setSealed(true)
              native.logMixpanelEvent("html_lodestone_sealed")
            }, 360),
          )
        }
      }
    },
    [total],
  )

  const moveTo = useCallback(
    (px: number, py: number) => {
      const { w, h } = dims.current
      const x = clamp(px, 0, w)
      const y = clamp(py, 0, h)
      setPuck({ x, y })
      pullAt(x, y)
    },
    [pullAt],
  )

  // Auto-demo for passive viewers: sweep the lodestone through every photo, then
  // rest at the vault as it seals. A gentle scripted tour of the mechanic.
  const autoSweep = useCallback(() => {
    if (touched.current || sealedRef.current) return
    native.logMixpanelEvent("html_lodestone_autodemo")
    const { w, h } = dims.current
    const stops = [
      restPos(),
      ...PHOTOS.map((p) => ({ x: (p.x / 100) * w, y: (p.y / 100) * h })),
      restPos(),
    ]
    const SEG = 430 // ms per hop
    let seg = 0
    let segStart = 0
    const step = (t: number) => {
      if (touched.current) {
        stopRaf()
        return
      }
      if (!segStart) segStart = t
      const from = stops[seg]
      const to = stops[seg + 1]
      const p = clamp((t - segStart) / SEG, 0, 1)
      const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2 // easeInOut
      moveTo(from.x + (to.x - from.x) * e, from.y + (to.y - from.y) * e)
      if (p >= 1) {
        seg += 1
        segStart = 0
        if (seg >= stops.length - 1) {
          stopRaf()
          return
        }
      }
      rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
  }, [moveTo, restPos])

  useEffect(() => {
    if (reduceMotion) {
      setShowClose(true)
      return
    }
    pushTimer(window.setTimeout(() => setHint(true), 1100))
    pushTimer(window.setTimeout(() => setShowClose(true), 2400))
    pushTimer(window.setTimeout(() => autoSweep(), 2600))
    return () => {
      stopRaf()
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- drag the lodestone -----------------------------------------------------
  const localPoint = (clientX: number, clientY: number) => {
    const el = fieldRef.current
    if (!el) return { x: 0, y: 0 }
    const r = el.getBoundingClientRect()
    return { x: clientX - r.left, y: clientY - r.top }
  }

  const beginDrag = (clientX: number, clientY: number) => {
    if (reduceMotion) return
    touched.current = true
    stopRaf()
    setHint(false)
    setDragging(true)
    const { x, y } = localPoint(clientX, clientY)
    moveTo(x, y)
  }

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    beginDrag(e.clientX, e.clientY)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging || e.buttons === 0) return
    const { x, y } = localPoint(e.clientX, e.clientY)
    moveTo(x, y)
  }
  const endDrag = () => {
    if (!dragging) return
    setDragging(false)
    // ease the lodestone back to its rest post at the vault
    const start = { ...puckRef.current }
    const rest = restPos()
    const DUR = 320
    let t0 = 0
    const step = (t: number) => {
      if (!t0) t0 = t
      const p = clamp((t - t0) / DUR, 0, 1)
      const e = 1 - Math.pow(1 - p, 3)
      setPuck({
        x: start.x + (rest.x - start.x) * e,
        y: start.y + (rest.y - start.y) * e,
      })
      if (p < 1) rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
  }

  const finishAll = () => {
    if (sealedRef.current) return
    touched.current = true
    stopRaf()
    const all = new Set(PHOTOS.map((p) => p.id))
    capturedRef.current = all
    setCaptured(all)
    setPuck(restPos())
    pushTimer(
      window.setTimeout(() => {
        sealedRef.current = true
        setSealed(true)
      }, 300),
    )
  }

  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct
  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    native.logMixpanelEvent("html_lodestone_subscribe", { plan })
    if (selectedProduct) native.subscribe(selectedProduct)
  }
  const openTerms = () => native.postNativeEvent("terms")
  const openPrivacy = () => native.postNativeEvent("privacy")

  const capCount = captured.size
  const sourceCleared = (key: SourceKey) =>
    PHOTOS.filter((p) => p.src === key).every((p) => captured.has(p.id))

  const dockIndexOf = (id: string) => {
    // stable dock order = capture order approximated by PHOTOS order
    const order = PHOTOS.filter((p) => captured.has(p.id)).map((p) => p.id)
    return order.indexOf(id)
  }

  return (
    <div className={`${styles.root} ${sealed ? styles.rootSealed : ""}`}>
      <div className={styles.aura} aria-hidden="true" />

      {/* ---------- header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="11.4" r="2.1" fill="currentColor" />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- title ---------- */}
      <div className={styles.intro}>
        <span className={styles.eyebrow}>Feature · Move to Vault</span>
        <h1 className={styles.title}>
          Draw every private
          <br />
          photo home.
        </h1>
        <p className={styles.sub}>
          Right now they’re scattered where anyone can reach — your Camera Roll,
          iCloud, a shared album. Sweep the lodestone and each one is pulled
          into a vault that seals behind your Face&nbsp;ID or PIN.
        </p>
      </div>

      {/* ---------- the magnet field ---------- */}
      <div
        ref={fieldRef}
        className={`${styles.field} ${dragging ? styles.fieldDrag : ""}`}
        role="group"
        aria-label="Draw your private photos into the vault"
      >
        {/* live readout */}
        <div className={styles.readout}>
          <span className={styles.readoutDot} aria-hidden="true" />
          {sealed ? "SEALED" : capCount > 0 ? "DRAWING IN" : "EXPOSED"}
          <span className={styles.readoutCount} aria-live="polite">
            {capCount}
            <em>/{total} in vault</em>
          </span>
        </div>

        {/* exposed source zones */}
        {SOURCES.map((s) => {
          const cleared = sourceCleared(s.key)
          return (
            <span
              key={s.key}
              className={`${styles.source} ${cleared ? styles.sourceCleared : ""}`}
              style={{ left: `${s.x}%`, top: `${s.y}%` }}
            >
              <svg
                viewBox="0 0 24 24"
                width="11"
                height="11"
                aria-hidden="true"
              >
                {cleared ? (
                  <path
                    d="M5 12.5l4 4 10-10.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ) : (
                  <>
                    <path
                      d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                    />
                    <circle cx="12" cy="12" r="2.4" fill="currentColor" />
                  </>
                )}
              </svg>
              <span className={styles.sourceLabel}>{s.label}</span>
              <em className={styles.sourceState}>
                {cleared ? "removed" : "exposed"}
              </em>
            </span>
          )
        })}

        {/* the photos */}
        {PHOTOS.map((p) => {
          const isCap = captured.has(p.id)
          const di = dockIndexOf(p.id)
          const style = isCap
            ? ({
                left: "50%",
                top: "44%",
                ["--dx" as any]: `${(di - (total - 1) / 2) * 16}px`,
                ["--dy" as any]: `${Math.abs(di - (total - 1) / 2) * -3}px`,
                ["--rot" as any]: `${(di - (total - 1) / 2) * 7}deg`,
                zIndex: 30 + di,
              } as React.CSSProperties)
            : ({
                left: `${p.x}%`,
                top: `${p.y}%`,
                zIndex: 5,
              } as React.CSSProperties)
          return (
            <span
              key={p.id}
              className={`${styles.photo} ${isCap ? styles.photoDocked : ""}`}
              style={style}
              aria-hidden="true"
            >
              <span
                className={styles.photoImg}
                style={{ backgroundImage: `url(/byheart/${p.img}.jpg)` }}
              />
            </span>
          )
        })}

        {/* the vault at the centre — the destination everything is drawn into */}
        <div
          className={`${styles.vault} ${sealed ? styles.vaultSealed : ""}`}
          aria-hidden="true"
        >
          <span className={styles.vaultSlot} />
          <div className={styles.vaultInner}>
            <motion.span
              className={styles.vaultLock}
              initial={false}
              animate={sealed ? { y: 0, opacity: 1 } : { y: 2, opacity: 0.6 }}
              transition={{ type: "spring", stiffness: 320, damping: 17 }}
            >
              <svg viewBox="0 0 24 24" width="19" height="19">
                <path
                  d={
                    sealed
                      ? "M7 11V8a5 5 0 0 1 10 0v3"
                      : "M7 11V8a5 5 0 0 1 9.6-1.9"
                  }
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <rect
                  x="5"
                  y="11"
                  width="14"
                  height="9"
                  rx="2.3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                />
                <circle cx="12" cy="15.4" r="1.5" fill="currentColor" />
              </svg>
            </motion.span>
            <span className={styles.vaultLabel}>
              {sealed ? "Sealed · Face ID / PIN" : "Your vault"}
            </span>
          </div>
        </div>

        {/* the lodestone */}
        {!reduceMotion && (
          <button
            className={`${styles.lodestone} ${dragging ? styles.lodestoneOn : ""} ${
              hint ? styles.lodestoneHint : ""
            }`}
            style={{ left: `${puck.x}px`, top: `${puck.y}px` }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            aria-label="Drag the lodestone to draw photos into the vault"
          >
            <span className={styles.lodestoneField} aria-hidden="true" />
            <span className={styles.lodestoneCore} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path
                  d="M6 4v7a6 6 0 0 0 12 0V4h-3.4v7a2.6 2.6 0 0 1-5.2 0V4H6z"
                  fill="currentColor"
                />
                <rect x="6" y="4" width="3.4" height="3" fill="#e0533d" />
                <rect x="14.6" y="4" width="3.4" height="3" fill="#e0533d" />
              </svg>
            </span>
          </button>
        )}
      </div>

      {/* ---------- action row ---------- */}
      <div className={styles.midRow} aria-live="polite">
        {sealed ? (
          <span className={styles.midDone}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            All {total} sealed in your vault — off every other app
          </span>
        ) : (
          <button
            className={styles.sweepBtn}
            onClick={finishAll}
            disabled={!!reduceMotion}
          >
            <span className={styles.sweepDot} aria-hidden="true" />
            Draw them all in
          </button>
        )}
        <span className={styles.honest}>
          Photos are moved into the vault — relocated, never deleted.
        </span>
      </div>

      {/* ---------- protections ---------- */}
      <div className={styles.protects}>
        {PROTECTS.map((p, i) => (
          <span
            key={p.k}
            className={`${styles.protect} ${sealed ? styles.protectOn : ""}`}
            style={{ transitionDelay: `${140 + i * 90}ms` }}
          >
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {p.label}
          </span>
        ))}
      </div>

      {/* ---------- pricing (yearly featured) ---------- */}
      <div className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbon}>SAVE {savingsPct}%</span>
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planMeta}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                7 days free · then {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planRight}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planName}>Monthly</span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={handleSubscribe}>
        <span className={styles.ctaMain}>
          {plan === "yearly" ? "Try 7 days free" : "Continue"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `Then ${yearlyPrice}/year · cancel anytime`
            : `${monthlyPrice}/month · cancel anytime`}
        </span>
      </button>

      <div className={styles.legal}>
        <button onClick={openTerms}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={openPrivacy}>Privacy</button>
        <span aria-hidden="true">·</span>
        <span>Auto-renews until cancelled</span>
      </div>
    </div>
  )
}

export default LodestonePaywall
